import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff, ChevronUp, ChevronDown, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../../lib/api";
import ImageUploader from "./ImageUploader";

const NEW_CATEGORY_VALUE = "__new__";

const emptyVariant = () => ({ size: "", color: "", color_hex: "", price: "" });

const emptyForm = {
    id: null,
    name: "",
    description: "",
    image: "",
    price: "",
    category: "General",
    order: 0,
    badge: "Personalizable",
    whatsapp_message: "",
    variants: [],
    active: true,
};

export default function ProductsManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [activeCat, setActiveCat] = useState("Todo");
    const [creatingCategory, setCreatingCategory] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/products?include_inactive=true");
            setItems(data);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    // Unique categories derived from existing products (excluding empty ones)
    const categories = useMemo(() => {
        const set = new Set();
        items.forEach((p) => {
            if (p.category && p.category.trim()) set.add(p.category.trim());
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
    }, [items]);

    const filteredItems = useMemo(() => {
        if (activeCat === "Todo") return items;
        return items.filter((p) => p.category === activeCat);
    }, [items, activeCat]);

    const openAdd = () => {
        const defaultCat =
            activeCat !== "Todo" ? activeCat : categories[0] || "General";
        setForm({ ...emptyForm, category: defaultCat });
        setCreatingCategory(false);
        setNewCategoryName("");
        setModalOpen(true);
    };

    const openEdit = (item) => {
        setForm({
            id: item.id,
            name: item.name,
            description: item.description,
            image: item.image,
            price: item.price || "",
            category: item.category || "General",
            order: item.order || 0,
            badge: item.badge || "Personalizable",
            whatsapp_message: item.whatsapp_message || "",
            variants: Array.isArray(item.variants)
                ? item.variants.map((v) => ({
                      size: v.size || "",
                      color: v.color || "",
                      color_hex: v.color_hex || "",
                      price: v.price || "",
                  }))
                : [],
            active: item.active !== false,
        });
        setCreatingCategory(false);
        setNewCategoryName("");
        setModalOpen(true);
    };

    // ---------- Variant helpers ----------
    const addVariant = () =>
        setForm((f) => ({ ...f, variants: [...f.variants, emptyVariant()] }));

    const updateVariant = (idx, key, value) =>
        setForm((f) => ({
            ...f,
            variants: f.variants.map((v, i) =>
                i === idx ? { ...v, [key]: value } : v,
            ),
        }));

    const removeVariant = (idx) =>
        setForm((f) => ({
            ...f,
            variants: f.variants.filter((_, i) => i !== idx),
        }));

    const handleCategoryChange = (val) => {
        if (val === NEW_CATEGORY_VALUE) {
            setCreatingCategory(true);
            setNewCategoryName("");
            return;
        }
        setCreatingCategory(false);
        setForm((f) => ({ ...f, category: val }));
    };

    const confirmNewCategory = () => {
        const name = newCategoryName.trim();
        if (!name) {
            toast.error("Escribe un nombre para el apartado.");
            return;
        }
        setForm((f) => ({ ...f, category: name }));
        setCreatingCategory(false);
        setNewCategoryName("");
    };

    const onSave = async (e) => {
        e.preventDefault();
        if (!form.name || !form.description || !form.image) {
            toast.error("Completa nombre, descripción e imagen.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: form.name,
                description: form.description,
                image: form.image,
                price: form.price,
                category: form.category,
                order: Number(form.order) || 0,
                badge: form.badge,
                whatsapp_message:
                    form.whatsapp_message || `Hola Silvia, me interesa ${form.name}.`,
                variants: form.variants
                    .map((v) => ({
                        size: (v.size || "").trim() || null,
                        color: (v.color || "").trim() || null,
                        color_hex: (v.color_hex || "").trim() || null,
                        price: (v.price || "").trim() || null,
                    }))
                    .filter(
                        (v) => v.size || v.color || v.price,
                    ),
                active: !!form.active,
            };
            if (form.id) {
                await api.put(`/products/${form.id}`, payload);
                toast.success("Producto actualizado");
            } else {
                await api.post("/products", payload);
                toast.success("Producto añadido");
            }
            setModalOpen(false);
            setForm(emptyForm);
            await load();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async (item) => {
        if (!window.confirm(`¿Eliminar el producto "${item.name}"?`)) return;
        try {
            await api.delete(`/products/${item.id}`);
            toast.success("Producto eliminado");
            setItems((prev) => prev.filter((i) => i.id !== item.id));
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const toggleActive = async (item) => {
        try {
            const next = item.active === false ? true : false;
            await api.put(`/products/${item.id}`, { active: next });
            toast.success(next ? "Producto activado" : "Producto oculto");
            setItems((prev) =>
                prev.map((i) => (i.id === item.id ? { ...i, active: next } : i)),
            );
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const moveItem = async (item, direction) => {
        // Reorder only inside currently visible (filtered) list, then persist
        // the global list order so the backend keeps a stable index.
        const visible = filteredItems;
        const idx = visible.findIndex((i) => i.id === item.id);
        const swapIdx = direction === "up" ? idx - 1 : idx + 1;
        if (idx < 0 || swapIdx < 0 || swapIdx >= visible.length) return;
        const swapItem = visible[swapIdx];
        // Build new global order from current items, applying the swap.
        const newOrder = items.map((i) => {
            if (i.id === item.id) return swapItem;
            if (i.id === swapItem.id) return item;
            return i;
        });
        setItems(newOrder);
        try {
            await api.post("/products/reorder", {
                ids: newOrder.map((i) => i.id),
            });
        } catch (err) {
            toast.error(formatError(err));
            load();
        }
    };

    return (
        <section data-testid="products-manager">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
                <div>
                    <h2 className="font-display text-3xl text-[#1C1A18]">
                        Gestión de Productos
                    </h2>
                    <p className="text-sm text-[#6b665f] font-light mt-2">
                        Añade, edita o elimina los productos personalizados.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={openAdd}
                    data-testid="products-add-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 btn-gold text-[10px] uppercase self-start"
                >
                    <Plus size={14} /> Nuevo producto
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="animate-spin text-[#C4A882]" size={32} />
                </div>
            ) : items.length === 0 ? (
                <div className="text-center py-20 text-[#6b665f] font-light text-sm">
                    No hay productos todavía. Añade el primero.
                </div>
            ) : (
                <>
                    {categories.length > 0 && (
                        <div
                            className="flex flex-wrap gap-x-6 gap-y-2 mb-8"
                            data-testid="products-admin-filters"
                        >
                            <button
                                type="button"
                                onClick={() => setActiveCat("Todo")}
                                className={`chip ${
                                    activeCat === "Todo" ? "active" : ""
                                }`}
                                data-testid="products-admin-filter-todo"
                            >
                                Todo{" "}
                                <span className="ml-1 text-[#C4A882]">
                                    ({items.length})
                                </span>
                            </button>
                            {categories.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setActiveCat(c)}
                                    className={`chip ${
                                        activeCat === c ? "active" : ""
                                    }`}
                                    data-testid={`products-admin-filter-${c.toLowerCase()}`}
                                >
                                    {c}{" "}
                                    <span className="ml-1 text-[#C4A882]">
                                        (
                                        {
                                            items.filter(
                                                (i) => i.category === c,
                                            ).length
                                        }
                                        )
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}

                    {filteredItems.length === 0 ? (
                        <div className="text-center py-20 text-[#6b665f] font-light text-sm">
                            No hay productos en este apartado todavía.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {filteredItems.map((item) => (
                        <article
                            key={item.id}
                            data-testid={`product-item-${item.id}`}
                            className={`bg-white border border-[#f0ece4] flex flex-col ${item.active === false ? "opacity-60" : ""}`}
                        >
                            <div className="relative aspect-[4/5] overflow-hidden bg-[#f0ece4]">
                                <img
                                    src={item.image}
                                    alt={item.name}
                                    className="w-full h-full object-cover"
                                />
                                {item.badge && (
                                    <span className="absolute top-3 right-3 bg-[#C4A882] text-white text-[9px] uppercase tracking-[0.24em] px-2.5 py-1">
                                        {item.badge}
                                    </span>
                                )}
                                {item.active === false && (
                                    <span className="absolute top-3 left-3 bg-[#1C1A18] text-white text-[9px] uppercase tracking-[0.24em] px-2.5 py-1">
                                        Oculto
                                    </span>
                                )}
                                {/* Reorder controls (top-right) */}
                                <div className="absolute bottom-3 left-3 flex flex-col gap-1">
                                    <button
                                        type="button"
                                        onClick={() => moveItem(item, "up")}
                                        aria-label="Subir"
                                        data-testid={`product-move-up-${item.id}`}
                                        className="w-7 h-7 bg-white/95 backdrop-blur-sm border border-[#e6e2db] text-[#1C1A18] hover:bg-[#1C1A18] hover:text-white inline-flex items-center justify-center"
                                    >
                                        <ChevronUp size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => moveItem(item, "down")}
                                        aria-label="Bajar"
                                        data-testid={`product-move-down-${item.id}`}
                                        className="w-7 h-7 bg-white/95 backdrop-blur-sm border border-[#e6e2db] text-[#1C1A18] hover:bg-[#1C1A18] hover:text-white inline-flex items-center justify-center"
                                    >
                                        <ChevronDown size={14} />
                                    </button>
                                </div>
                            </div>
                            <div className="p-5 flex-1 flex flex-col">
                                <div className="flex items-start justify-between gap-2">
                                    <h3 className="font-display text-lg text-[#1C1A18] leading-tight">
                                        {item.name}
                                    </h3>
                                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#C4A882] shrink-0">
                                        {item.price || "—"}
                                    </span>
                                </div>
                                <p className="text-xs text-[#6b665f] font-light mt-2 line-clamp-3">
                                    {item.description}
                                </p>
                                <p className="text-[10px] tracking-[0.28em] uppercase text-[#9a958c] mt-3">
                                    {item.category} · #{item.order}
                                    {Array.isArray(item.variants) && item.variants.length > 0
                                        ? ` · ${item.variants.length} variante${item.variants.length === 1 ? "" : "s"}`
                                        : ""}
                                </p>
                                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#f0ece4]">
                                    <button
                                        type="button"
                                        onClick={() => openEdit(item)}
                                        data-testid={`product-edit-${item.id}`}
                                        className="flex-1 inline-flex items-center justify-center gap-2 py-2 text-[10px] uppercase tracking-[0.24em] border border-[#1C1A18] hover:bg-[#1C1A18] hover:text-[#FAFAF8] transition-colors"
                                    >
                                        <Pencil size={12} /> Editar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => toggleActive(item)}
                                        data-testid={`product-toggle-${item.id}`}
                                        className="p-2.5 border border-[#f0ece4] text-[#6b665f] hover:text-[#1C1A18] hover:border-[#1C1A18]"
                                        aria-label={item.active === false ? "Activar" : "Ocultar"}
                                        title={item.active === false ? "Activar" : "Ocultar"}
                                    >
                                        {item.active === false ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => onDelete(item)}
                                        data-testid={`product-delete-${item.id}`}
                                        className="p-2.5 border border-[#f0ece4] text-[#6b665f] hover:text-red-600 hover:border-red-200"
                                        aria-label="Eliminar"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}
                </>
            )}

            {modalOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    data-testid="products-modal"
                    className="fixed inset-0 z-[100] bg-[#1C1A18]/80 flex items-center justify-center p-4 overflow-y-auto"
                    onClick={() => !saving && setModalOpen(false)}
                >
                    <form
                        onSubmit={onSave}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-[#FAFAF8] w-full max-w-3xl my-12 p-8 lg:p-10 grid grid-cols-1 md:grid-cols-2 gap-8"
                    >
                        <div>
                            <h3 className="font-display text-2xl text-[#1C1A18] mb-4">
                                {form.id ? "Editar producto" : "Nuevo producto"}
                            </h3>
                            <ImageUploader
                                value={form.image}
                                onChange={(v) => setForm((f) => ({ ...f, image: v }))}
                                testIdPrefix="product-image"
                            />
                        </div>

                        <div className="space-y-5">
                            <div>
                                <label className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2">
                                    Nombre
                                </label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, name: e.target.value }))
                                    }
                                    className="field"
                                    required
                                    data-testid="product-name-input"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2">
                                    Descripción
                                </label>
                                <textarea
                                    rows={3}
                                    value={form.description}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, description: e.target.value }))
                                    }
                                    className="field"
                                    required
                                    data-testid="product-description-input"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2">
                                        Precio
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Desde 60€"
                                        value={form.price}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, price: e.target.value }))
                                        }
                                        className="field"
                                        data-testid="product-price-input"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2">
                                        Orden
                                    </label>
                                    <input
                                        type="number"
                                        value={form.order}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, order: e.target.value }))
                                        }
                                        className="field"
                                        data-testid="product-order-input"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2">
                                        Apartado
                                    </label>
                                    {creatingCategory ? (
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={newCategoryName}
                                                onChange={(e) =>
                                                    setNewCategoryName(
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="Nombre del apartado"
                                                className="field flex-1"
                                                autoFocus
                                                data-testid="product-new-category-input"
                                            />
                                            <button
                                                type="button"
                                                onClick={confirmNewCategory}
                                                className="px-3 py-2 bg-[#1C1A18] text-white text-[10px] uppercase"
                                                data-testid="product-new-category-confirm"
                                            >
                                                OK
                                            </button>
                                        </div>
                                    ) : (
                                        <select
                                            value={form.category}
                                            onChange={(e) =>
                                                handleCategoryChange(
                                                    e.target.value,
                                                )
                                            }
                                            className="field"
                                            data-testid="product-category-select"
                                        >
                                            {/* Ensure current value is selectable
                                                even if it's not yet in the list. */}
                                            {form.category &&
                                                !categories.includes(
                                                    form.category,
                                                ) && (
                                                    <option
                                                        value={form.category}
                                                    >
                                                        {form.category}
                                                    </option>
                                                )}
                                            {categories.map((c) => (
                                                <option key={c} value={c}>
                                                    {c}
                                                </option>
                                            ))}
                                            <option
                                                value={NEW_CATEGORY_VALUE}
                                                data-testid="product-category-new-option"
                                            >
                                                + Nuevo apartado…
                                            </option>
                                        </select>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2">
                                        Badge
                                    </label>
                                    <input
                                        type="text"
                                        value={form.badge}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, badge: e.target.value }))
                                        }
                                        className="field"
                                        data-testid="product-badge-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2">
                                    Mensaje WhatsApp (opcional)
                                </label>
                                <input
                                    type="text"
                                    value={form.whatsapp_message}
                                    onChange={(e) =>
                                        setForm((f) => ({
                                            ...f,
                                            whatsapp_message: e.target.value,
                                        }))
                                    }
                                    className="field"
                                    placeholder="Hola Silvia, me interesa…"
                                    data-testid="product-whatsapp-input"
                                />
                            </div>

                            {/* ---------------- Variantes ---------------- */}
                            <div
                                className="border-t border-[#e6e2db] pt-5"
                                data-testid="variants-editor"
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <label className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f]">
                                            Variantes (medidas / colores)
                                        </label>
                                        <p className="text-[11px] text-[#9a958c] font-light mt-1">
                                            Añade combinaciones (ej. 20×30 · Negro · 25€). Cada
                                            campo es opcional.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={addVariant}
                                        data-testid="variant-add-btn"
                                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.32em] uppercase text-[#1C1A18] border border-[#1C1A18] px-3 py-2 hover:bg-[#1C1A18] hover:text-white"
                                    >
                                        <Plus size={12} /> Añadir
                                    </button>
                                </div>

                                {form.variants.length === 0 ? (
                                    <p className="text-[11px] text-[#9a958c] font-light italic">
                                        Sin variantes — el producto se mostrará con un único precio.
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        {form.variants.map((v, idx) => (
                                            <div
                                                key={idx}
                                                className="grid grid-cols-[1fr_1fr_44px_1fr_36px] gap-2 items-center"
                                                data-testid={`variant-row-${idx}`}
                                            >
                                                <input
                                                    type="text"
                                                    value={v.size}
                                                    onChange={(e) =>
                                                        updateVariant(idx, "size", e.target.value)
                                                    }
                                                    placeholder="Medida (20x30)"
                                                    className="field text-xs"
                                                    data-testid={`variant-size-${idx}`}
                                                />
                                                <input
                                                    type="text"
                                                    value={v.color}
                                                    onChange={(e) =>
                                                        updateVariant(idx, "color", e.target.value)
                                                    }
                                                    placeholder="Color (Negro)"
                                                    className="field text-xs"
                                                    data-testid={`variant-color-${idx}`}
                                                />
                                                <input
                                                    type="color"
                                                    value={v.color_hex || "#000000"}
                                                    onChange={(e) =>
                                                        updateVariant(
                                                            idx,
                                                            "color_hex",
                                                            e.target.value,
                                                        )
                                                    }
                                                    title="Hex del color"
                                                    className="h-10 w-full border border-[#e6e2db] cursor-pointer bg-white p-0"
                                                    data-testid={`variant-hex-${idx}`}
                                                />
                                                <input
                                                    type="text"
                                                    value={v.price}
                                                    onChange={(e) =>
                                                        updateVariant(idx, "price", e.target.value)
                                                    }
                                                    placeholder="Precio (25€)"
                                                    className="field text-xs"
                                                    data-testid={`variant-price-${idx}`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeVariant(idx)}
                                                    aria-label="Eliminar variante"
                                                    data-testid={`variant-remove-${idx}`}
                                                    className="w-9 h-9 inline-flex items-center justify-center border border-[#f0ece4] text-[#6b665f] hover:text-red-600 hover:border-red-200"
                                                >
                                                    <XIcon size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Activo */}
                            <label className="flex items-center gap-3 text-xs text-[#1C1A18] cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={!!form.active}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, active: e.target.checked }))
                                    }
                                    className="w-4 h-4 accent-[#C4A882]"
                                    data-testid="product-active-toggle"
                                />
                                <span className="tracking-[0.28em] uppercase text-[10px]">
                                    Producto visible en la web
                                </span>
                            </label>

                            <div className="flex flex-col sm:flex-row gap-3 pt-3">
                                <button
                                    type="submit"
                                    disabled={saving}
                                    data-testid="product-save-btn"
                                    className="flex-1 px-6 py-3 btn-gold text-[10px] uppercase disabled:opacity-60"
                                >
                                    {saving
                                        ? "Guardando…"
                                        : form.id
                                          ? "Guardar cambios"
                                          : "Añadir producto"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    disabled={saving}
                                    data-testid="product-cancel-btn"
                                    className="px-6 py-3 btn-outline-ink text-[10px] uppercase"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            )}
        </section>
    );
}
